import React, { useState } from 'react';
import { 
    Stack, 
    Button, 
    Chip, 
    Typography, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    Alert,
    Box
} from '@mui/material';
import { 
    Lock, 
    LockOpen, 
    Security, 
    Warning, 
    CheckCircle,
    Cancel
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { dateFormatter } from '../../settings/themeSettings';
import { notify } from '../../settings/toastSetting';
import { 
    preCloseCircuit, 
    finalCloseCircuit, 
    reopenPreClosedCircuit 
} from '../../data/slices/circuitSlice';
import './ClosureManagement.scss';

const ClosureManagement = ({ dossier, onStateChange, type = 'circuit' }) => {
    const { me } = useSelector((state) => state.me);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });
    const [loading, setLoading] = useState(false);

    // Vérifier les habilitations
    const isAdmin = /ADMIN/.test(me?.profil || '');
    const isProduction = /PRODUCTION/.test(me?.profil || '');
    const canPreClose = isProduction || isAdmin;
    const canFinalClose = isAdmin;
    const canReopen = isAdmin;

    // États possibles du dossier
    const getClosureState = () => {
        if (dossier?.cloture_definitive) {
            return 'final_closed'; // Clôture définitive
        } else if (dossier?.pre_cloture) {
            return 'pre_closed'; // Pré-clôture
        } else {
            return 'open'; // Ouvert
        }
    };

    const closureState = getClosureState();

    const handleAction = async (action) => {
        setLoading(true);
        try {
            let response;
            switch (action) {
                case 'pre_close':
                    response = await preCloseCircuit(dossier.id);
                    break;
                case 'final_close':
                    response = await finalCloseCircuit(dossier.id);
                    break;
                case 'reopen':
                    response = await reopenPreClosedCircuit(dossier.id);
                    break;
                default:
                    throw new Error('Action non reconnue');
            }

            if (response.status >= 200 && response.status < 300) {
                const updatedDossier = response.data;
                notify('Opération réussie!', 'success');
                if (onStateChange) {
                    onStateChange(updatedDossier);
                }
            } else {
                throw new Error('Erreur lors de l\'opération');
            }
        } catch (error) {
            notify('Erreur lors de l\'opération: ' + error.message, 'error');
        } finally {
            setLoading(false);
            setConfirmDialog({ open: false, action: null, title: '', message: '' });
        }
    };

    const openConfirmDialog = (action, title, message) => {
        setConfirmDialog({ open: true, action, title, message });
    };

    const renderClosureStatus = () => {
        switch (closureState) {
            case 'open':
                return (
                    <Chip 
                        icon={<LockOpen />} 
                        label="Dossier ouvert" 
                        color="success" 
                        variant="outlined"
                        size="small"
                    />
                );
            case 'pre_closed':
                return (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip 
                            icon={<Lock />} 
                            label="Pré-clôturé" 
                            color="warning" 
                            size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                            {dossier.pre_cloture_by && `Par: ${dossier.pre_cloture_by.nomComplet}`}
                            {dossier.pre_cloture_date && ` le ${dateFormatter(dossier.pre_cloture_date)}`}
                        </Typography>
                    </Stack>
                );
            case 'final_closed':
                return (
                    <Stack direction="column" spacing={0.5}>
                        <Chip 
                            icon={<Security />} 
                            label="Clôture définitive" 
                            color="error" 
                            size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                            {dossier.cloture_definitive_by && `Par: ${dossier.cloture_definitive_by.nomComplet}`}
                            {dossier.cloture_definitive_date && ` le ${dateFormatter(dossier.cloture_definitive_date)}`}
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                            🔒 IRRÉVERSIBLE
                        </Typography>
                    </Stack>
                );
            default:
                return null;
        }
    };

    const renderActionButtons = () => {
        const buttons = [];

        switch (closureState) {
            case 'open':
                if (canPreClose) {
                    buttons.push(
                        <Button
                            key="pre-close"
                            variant="contained"
                            color="warning"
                            startIcon={<Lock />}
                            disabled={loading}
                            onClick={() => openConfirmDialog(
                                'pre_close',
                                'Pré-clôture du dossier',
                                `Voulez-vous effectuer la pré-clôture de ce ${type} ? Cette action reste réversible par un administrateur.`
                            )}
                        >
                            Pré-clôturer
                        </Button>
                    );
                }
                break;

            case 'pre_closed':
                if (canFinalClose) {
                    buttons.push(
                        <Button
                            key="final-close"
                            variant="contained"
                            color="error"
                            startIcon={<Security />}
                            disabled={loading}
                            onClick={() => openConfirmDialog(
                                'final_close',
                                'Clôture définitive',
                                `⚠️ ATTENTION: Voulez-vous effectuer la clôture DÉFINITIVE de ce ${type} ? Cette action est IRRÉVERSIBLE et ne peut plus être annulée.`
                            )}
                        >
                            Clôture définitive
                        </Button>
                    );
                }

                if (canReopen) {
                    buttons.push(
                        <Button
                            key="reopen"
                            variant="outlined"
                            color="primary"
                            startIcon={<LockOpen />}
                            disabled={loading}
                            onClick={() => openConfirmDialog(
                                'reopen',
                                'Réouverture du dossier',
                                `Voulez-vous rouvrir ce ${type} et annuler sa pré-clôture ?`
                            )}
                        >
                            Rouvrir
                        </Button>
                    );
                }
                break;

            case 'final_closed':
                // Aucune action possible sur un dossier définitivement clôturé
                break;

            default:
                break;
        }

        return buttons;
    };

    return (
        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
            <Stack direction="column" spacing={2}>
                <Typography variant="h6" color="primary">
                    Gestion de la clôture
                </Typography>

                {renderClosureStatus()}

                {/* Informations sur les habilitations */}
                <Box sx={{ backgroundColor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        <strong>Vos habilitations:</strong>
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {canPreClose && (
                            <Chip 
                                label="Pré-clôture" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                            />
                        )}
                        {canFinalClose && (
                            <Chip 
                                label="Clôture définitive" 
                                size="small" 
                                color="error" 
                                variant="outlined"
                            />
                        )}
                        {canReopen && (
                            <Chip 
                                label="Réouverture" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                            />
                        )}
                    </Stack>
                </Box>

                {/* Boutons d'action */}
                <Stack direction="row" spacing={1}>
                    {renderActionButtons()}
                </Stack>

                {/* Légende des niveaux */}
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'black' }}>
                        Niveaux de clôture :
                    </Typography>
                    <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ color: 'black' }}>
                            • <strong>Pré-clôture:</strong> Effectuée par agent production, réversible par admin
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'black' }}>
                            • <strong>Clôture définitive:</strong> Effectuée par admin, IRRÉVERSIBLE
                        </Typography>
                    </Stack>
                </Box>
            </Stack>

            {/* Dialog de confirmation */}
            <Dialog 
                open={confirmDialog.open} 
                onClose={() => setConfirmDialog({ open: false, action: null, title: '', message: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {confirmDialog.action === 'final_close' ? <Warning color="error" /> : <CheckCircle color="primary" />}
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <Alert severity={confirmDialog.action === 'final_close' ? 'error' : 'info'} sx={{ mb: 2 }}>
                        {confirmDialog.message}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setConfirmDialog({ open: false, action: null, title: '', message: '' })}
                        startIcon={<Cancel />}
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={() => handleAction(confirmDialog.action)}
                        variant="contained"
                        color={confirmDialog.action === 'final_close' ? 'error' : 'primary'}
                        disabled={loading}
                        startIcon={confirmDialog.action === 'final_close' ? <Security /> : <CheckCircle />}
                    >
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClosureManagement;
